import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { isReevolHostname } from '@40seas/frontend/libs/configs/consts';
import { useFeatureToggleClient } from '../featureTogglesProvider/featureToggleProvider';
import { BRAND_40SEAS, BRAND_REEVOL, BrandConfig } from './types';
import { refreshFavicon } from './refreshFavicon';
import { resolveAssetUrl } from './publicUrl';

declare global {
    interface Window {
        __BRAND_NAME__?: string;
        __SETUP__?: { urls?: { PUBLIC_URL?: string } };
    }
}

const BrandConfigContext = createContext<BrandConfig>(BRAND_40SEAS);

export const useBrandConfig = () => useContext(BrandConfigContext);

export const BrandConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const featureToggleClient = useFeatureToggleClient();
    // Hostname detection takes priority so the Reevol login page (pre-auth,
    // before any feature toggle is evaluated) shows Reevol branding.
    // The feature toggle continues to work for post-login brand switching on
    // the 40Seas domain.
    const isReevol = isReevolHostname() || featureToggleClient.conduct('reevol-rebranding', false);
    const baseBrandConfig = isReevol ? BRAND_REEVOL : BRAND_40SEAS;

    const brandConfig = useMemo(
        () => ({
            ...baseBrandConfig,
            faviconUrl: resolveAssetUrl(baseBrandConfig.faviconUrl),
        }),
        [baseBrandConfig],
    );

    useEffect(() => {
        if (typeof window !== 'undefined' && window.__BRAND_NAME__ !== brandConfig.displayName) {
            window.__BRAND_NAME__ = brandConfig.displayName;
        }
    }, [brandConfig.displayName]);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        document.title = brandConfig.displayName;

        const controller = new AbortController();
        const normalizedFaviconUrl = new URL(brandConfig.faviconUrl, window.location.href).href;
        refreshFavicon(normalizedFaviconUrl, controller.signal);

        return () => controller.abort();
    }, [brandConfig.faviconUrl, brandConfig.displayName]);

    return <BrandConfigContext.Provider value={brandConfig}>{children}</BrandConfigContext.Provider>;
};
